import React from "react";
import { Shield, AlertTriangle, TrendingUp, Building2, ChevronRight } from "lucide-react";
import ComplianceStatus from "../graph/ComplianceStatus";
import SeverityDistribution from "../graph/SeverityDistribution";

const OrganizationView = ({ trendsData, cloudAccounts, onAccountSelect }) => {
  // Calculate organization metrics from trends data
  const calculateMetrics = () => {
    if (!trendsData || trendsData.length === 0) {
      return {
        totalAccounts: 0,
        totalFindings: { critical: 0, high: 0, medium: 0, low: 0 },
        avgCompliance: 0
      };
    }

    const accountsMap = new Map();
    let totalCritical = 0, totalHigh = 0, totalMedium = 0, totalLow = 0;

    trendsData.forEach(item => {
      const combinedId = item["organizationID#accountId"];
      if (combinedId) {
        const accountId = combinedId.split('#')[1];
        accountsMap.set(accountId, true);
        
        // Handle both string and object formats
        let severityDist = {};
        if (typeof item.severityDistribution === 'string') {
          severityDist = JSON.parse(item.severityDistribution || '{}');
        } else if (item.severityDistribution?.S) {
          severityDist = JSON.parse(item.severityDistribution.S || '{}');
        }
        
        totalCritical += severityDist.Critical || 0;
        totalHigh += severityDist.High || 0;
        totalMedium += severityDist.Medium || 0;
        totalLow += severityDist.Low || 0;
      }
    });

    return {
      totalAccounts: accountsMap.size,
      totalFindings: {
        critical: totalCritical,
        high: totalHigh,
        medium: totalMedium,
        low: totalLow
      }
    };
  };

  // Get account cards with metrics
  const getAccountSummary = () => {
    if (!trendsData) return [];
    
    const accountsMap = new Map();
    
    trendsData.forEach(item => {
      const combinedId = item["organizationID#accountId"];
      if (combinedId) {
        const accountId = combinedId.split('#')[1];
        const failedChecks = parseInt(item.failedChecks?.N || item.failedChecks || 0);
        const totalChecks = parseInt(item.totalChecks?.N || item.totalChecks || 0);
        
        // Handle both string and object formats
        let severityDist = {};
        if (typeof item.severityDistribution === 'string') {
          severityDist = JSON.parse(item.severityDistribution || '{}');
        } else if (item.severityDistribution?.S) {
          severityDist = JSON.parse(item.severityDistribution.S || '{}');
        }
        
        const scanDate = item.scanDate?.S || item.scanDate || '';
        
        if (!accountsMap.has(accountId)) {
          accountsMap.set(accountId, {
            accountId,
            alias: null,
            totalFindings: 0,
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
            complianceRate: 0,
            lastScan: scanDate,
            status: 'healthy'
          });
        }
        
        const account = accountsMap.get(accountId);
        account.totalFindings += failedChecks;
        account.critical += severityDist.Critical || 0;
        account.high += severityDist.High || 0;
        account.medium += severityDist.Medium || 0;
        account.low += severityDist.Low || 0;
        
        // Calculate compliance rate
        if (totalChecks > 0) {
          account.complianceRate = ((totalChecks - failedChecks) / totalChecks * 100).toFixed(1);
        }
        
        // Determine status
        if (account.critical > 0) account.status = 'critical';
        else if (account.high > 2) account.status = 'warning';
        else account.status = 'healthy';
      }
    });
    
    // Match with cloud accounts for aliases
    cloudAccounts.forEach(account => {
      if (accountsMap.has(account.awsAccountId)) {
        accountsMap.get(account.awsAccountId).alias = account.alias || account.description;
      }
    });
    
    return Array.from(accountsMap.values());
  };

  const metrics = calculateMetrics();
  const accounts = getAccountSummary();

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'critical': return '#ef4444';
      default: return '#64748b';
    }
  };

  const orgMetrics = [
    {
      title: 'Total Accounts',
      value: metrics.totalAccounts.toString(),
      icon: Building2,
      color: '#6D5BFF'
    },
    {
      title: 'Critical Findings',
      value: metrics.totalFindings.critical.toString(),
      icon: AlertTriangle,
      color: '#ef4444'
    },
    {
      title: 'High Findings',
      value: metrics.totalFindings.high.toString(),
      icon: Shield,
      color: '#f59e0b'
    },
    {
      title: 'Total Issues',
      value: (metrics.totalFindings.critical + metrics.totalFindings.high + metrics.totalFindings.medium).toString(),
      icon: TrendingUp,
      color: '#10b981'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Organization Metrics */}
      <div className="row g-4">
        {orgMetrics.map((metric, idx) => {
          const Icon = metric.icon;
          return (
            <div key={idx} className="col-md-6 col-lg-3">
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
                  padding: '24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px 0 rgba(31, 38, 135, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(31, 38, 135, 0.15)';
                }}
              >
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      backgroundColor: `${metric.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Icon size={24} style={{ color: metric.color }} />
                  </div>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '4px' }}>
                  {metric.title}
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1e293b' }}>
                  {metric.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="row g-4 mt-2">
        <div className="col-md-6">
          <div style={{ height: '420px', display: 'flex', flexDirection: 'column' }}>
            <ComplianceStatus data={trendsData || []} />
          </div>
        </div>
        <div className="col-md-6">
          <div style={{ height: '420px', display: 'flex', flexDirection: 'column' }}>
            <SeverityDistribution trendsData={trendsData || []} />
          </div>
        </div>
      </div>

      {/* Accounts Table */}
      <div
        className="mt-2"
        style={{
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          overflow: 'hidden'
        }}
      >
        <div style={{ padding: '24px' }}>
          <h5 style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
            AWS Accounts Overview
          </h5>
          <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: 0 }}>
            Click on an account to view detailed security findings
          </p>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '12px 24px', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' }}>Account</th>
                <th style={{ padding: '12px 24px', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' }}>Findings</th>
                <th style={{ padding: '12px 24px', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' }}>Compliance</th>
                <th style={{ padding: '12px 24px', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px 24px' }}></th>
              </tr>
            </thead>
            <tbody style={{ backgroundColor: 'white' }}>
              {accounts.map((account) => (
                <tr
                  key={account.accountId}
                  style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                  onClick={() => onAccountSelect(account.accountId)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={{ padding: '16px 24px' }}>
                    <div>
                      <div style={{ color: '#1e293b', fontWeight: '500' }}>{account.alias || account.accountId}</div>
                      {account.alias && (
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontFamily: 'monospace' }}>{account.accountId}</div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <div className="d-flex gap-2 flex-wrap">
                      {account.critical > 0 && (
                        <span className="badge" style={{ backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', fontSize: '0.75rem', padding: '4px 8px' }}>
                          {account.critical} C
                        </span>
                      )}
                      {account.high > 0 && (
                        <span className="badge" style={{ backgroundColor: '#fffbeb', color: '#f59e0b', border: '1px solid #fde68a', fontSize: '0.75rem', padding: '4px 8px' }}>
                          {account.high} H
                        </span>
                      )}
                      {account.medium > 0 && (
                        <span className="badge" style={{ backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde047', fontSize: '0.75rem', padding: '4px 8px' }}>
                          {account.medium} M
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <span style={{ color: '#1e293b', fontWeight: '500' }}>{account.complianceRate}%</span>
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(account.status)
                        }}
                      ></div>
                      <span style={{ fontSize: '0.875rem', color: getStatusColor(account.status), textTransform: 'capitalize', fontWeight: '500' }}>
                        {account.status}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <ChevronRight size={20} style={{ color: '#64748b' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrganizationView;
